<template>
    <div class="donotprint">
        <annotation-selection
            :scope="scope"
            :enabled="enabled && ! read_only"
            :current_annotation="currentAnnotation"
            :enable_private_annotations="enable_private_annotations"
            @create="create"
            @updateComments="updateComments"
        />
        <annotation-list
            :annotations="visibleAnnotations"
            :section_name="visibleSectionName"
            :section_id="visibleSectionId"
            :enabled="enabled"
            :self_id="self_id"
            :read_only="read_only"
            :self="self"
            @updateComments="updateComments"
            @deleteAnnotation="deleteAnnotation"
        />
    </div>
</template>

<script>
import axios from 'axios';
import * as Marklib from 'marklib';
import AnnotationSelection from './annotation-selection.vue';
import AnnotationList from './annotation-list.vue';

const TYPES = {
    highlight: {
        className: ['annotation', 'annotation-show', 'annotation-highlight'],
    },
    strikethrough: {
        className: ['annotation', 'annotation-show', 'annotation-strikethrough'],
    },
    task: {
        className: ['annotation', 'annotation-show', 'annotation-task'],
    },
    comment: {
        className: ['annotation', 'annotation-show', 'annotation-comment'],
    },
    private: {
        className: ['annotation', 'annotation-show', 'annotation-private'],
    },
    journal: {
        className: ['annotation', 'annotation-show', 'annotation-journal'],
    },
};

export default {
    components: {
        AnnotationSelection,
        AnnotationList,
    },

    props: {
        report_user: {},
        scope: { type: String, default: null },
        sections: { type: Object, default: () => { return {} }},
        section_names: { type: Object, default: () => { return {} }},
        enabled: true,
        show_author_ids: { type: Array, default: () => { return [] }},
        self_id: null,
        show_section_id: null,
        for_profile: {},
        read_only: { type: Boolean, default: false },
        self: '',
        enable_private_annotations: { type: Boolean, default: false },
    },

    data() {
        return {
            annotations: [],
            renderers: [],
            visibleSection: null,
            currentAnnotation: null,
        }
    },

    computed: {
        sectionIds() {
            return this.show_section_id ? [this.show_section_id] : Object.values(this.sections);
        },
        visibleSectionId() {
            return this.show_section_id
                ? this.show_section_id
                : this.sections[this.visibleSection];
        },
        visibleSectionName() {
            return this.section_names[this.visibleSectionId];
        },
        visibleAnnotations() {
            return this.visibleSectionId
                ? _.sortBy(
                    this.annotationsBySection[this.visibleSectionId],
                    ['position_top', 'position_left']
                )
                : [];
        },
        annotationsForVisibleAuthors() {
            return this.annotations.filter(annotation => {
                return this.show_author_ids.includes(annotation.author_id);
            });
        },
        annotationsBySection() {
            return this.annotationsForVisibleAuthors.length
                ? this.groupBy(this.annotationsForVisibleAuthors, 'report_section_id')
                : {};
        },
    },

    methods: {
        create(payload) {
            let renderer = this.createRenderer(payload.type, payload.section);
            this.renderers.push(payload.renderer);
            this.store(renderer, payload);
        },
        store(renderer, { type, section, range, created, positionTop, positionLeft }) {
            let contextSelector = '[data-annotation-section-id="'
                + section.dataset.annotationSectionId
                + '"]>';

            let render = renderer.renderWithRange(range);
            let result = render.serialize();
            let start = result.startContainerPath.split(';');
            let end = result.endContainerPath.split(';');

            let payload = {
                type: type,
                render_id: renderer.getId(),
                selected_text: render.text,
                start_path: contextSelector + start[0],
                start_text_node: start[1],
                start_character_offset: result.startOffset,
                end_path: contextSelector + end[0],
                end_text_node: end[1],
                end_character_offset: result.endOffset,
                position_top: positionTop,
                position_left: positionLeft,
                comment: null,
            };

            axios.post('/users/' + this.report_user.id
                + '/reportsections/' + section.dataset.annotationSectionId
                + '/annotations',
                payload
            ).then(response => {
                this.annotations.push(response.data);
                this.currentAnnotation = response.data;
                created(response.data.id);
            }).catch(error => {
                if (error?.response?.data?.message) {
                    alert('Error: ' + error.response.data.message);
                }

                console.log(error.response.data);
                renderer.destroy();
            });
        },
        display(annotation) {
            let context = document.querySelector('[data-annotation-section-id="' + annotation.report_section_id + '"]');

            if (! context) {
                return;
            }

            try {
                let renderer = this.createRenderer(annotation.type, document);
                renderer.setId(annotation.render_id);

                let startPath = [
                    annotation.start_path,
                    annotation.start_text_node,
                    annotation.start_character_offset
                ].join(';');

                let endPath = [
                    annotation.end_path,
                    annotation.end_text_node,
                    annotation.end_character_offset
                ].join(';');


                let renderedText = renderer.renderWithPath(startPath, endPath);

                // add actual ID to element containing data-selection-id, for anchor tag link
                let el = document.querySelector('[data-selection-id="' + annotation.render_id + '"]');

                if (el) {
                    el.id = annotation.render_id;
                }

                if (renderedText != annotation.selected_text) {
                    this.markArchivedStatus(annotation, true);
                    renderer.destroy();
                } else {
                    if (annotation.original_has_changed) {
                        this.markArchivedStatus(annotation, false);
                    }
                }
            } catch (error) {
                this.markArchivedStatus(annotation, true);

                if (typeof variable !== 'undefined') {
                    renderer.destroy();
                }
            }
        },
        markArchivedStatus(annotation, status) {
            annotation.original_has_changed = status;

            if (this.read_only) {
                return;
            }

            axios.patch('/users/' + this.report_user.id
                + '/reportsections/' + annotation.report_section_id
                + '/annotations/' + annotation.id,
                { original_has_changed: status }
            ).catch(error => {
                if (error.response.data.message) {
                    console.log('Error: ' + error.response.data.message);
                }
            });
        },
        createRenderer(type, context) {
            return new Marklib.Rendering(document, {
                className: TYPES[type].className,
            }, context);
        },
        groupBy(objectArray, property) {
            return objectArray.reduce(function (acc, obj) {
                let key = obj[property];

                if (! acc[key]) {
                    acc[key] = []
                }

                acc[key].push(obj);
                return acc;
            }, {});
        },
        updateComments({ id, comments }) {
            this.annotations.find(annotation => {
                return annotation.id === id;
            }).comments = comments;
        },
        hideAllAnnotations() {
            document.querySelectorAll('.annotation').forEach(element => {
                element.classList.remove('annotation-show');
            });
        },
        showAnnotationsForVisibleAuthors() {
            this.hideAllAnnotations();
            this.annotationsForVisibleAuthors.forEach(annotation => {
                let el = document.querySelector("[data-selection-id='" + annotation.render_id + "']");

                if (el) {
                    el.classList.add('annotation-show');
                }
            });
        },
        deleteAnnotation(id) {
            let index = this.annotations.findIndex(annotation => annotation.id == id);

            if (index > -1) {
                this.annotations.splice(index, 1);
            }
        },
    },

    created() {
        let first_section = document.querySelectorAll('div[data-annotation-section]')[0];

        if (first_section) {
            this.visibleSection = first_section.dataset.annotationSection;
        }

        this.$root.$on('reportNavChanged', (currentItem) => {
            if (currentItem && currentItem.hash.charAt(0) === '#') {
                this.visibleSection = currentItem.hash.substring(1);
            }
        });

        axios.get('/users/' + this.report_user.id + '/annotations', {
            params: {
                section_ids: this.sectionIds.join(','),
                for_profile: this.for_profile,
            }
        }).then(response => {
            this.annotations = response.data;
            this.annotations.forEach(annotation => {
                this.display(annotation);
            });
            this.$emit('fetched', this.annotations);
        }).catch(error => {
            console.log('Could not render annotation.');

            if (error.response) {
                console.log(error.response.data)
            };
        });
    },

    watch: {
        enabled(val, oldVal) {
            if (val) {
                this.showAnnotationsForVisibleAuthors();
            } else {
                this.hideAllAnnotations();
            }
        },
        show_author_ids(val, oldVal) {
            if (this.enabled) {
                this.showAnnotationsForVisibleAuthors();
            }
        },
    },
};
</script>

